/* ────────────────────────────────────────────────────────────────
   Consent + tracker loading (CA privacy hardening, October 2026)
   ────────────────────────────────────────────────────────────────
   Single source of truth for consent state and for loading every
   third-party tracker on the site. Ported from the WebPerfex /
   Bay Area Siding Co / GVD implementation (July 2026).

   Why: CIPA (§631 / §638.51) and federal Wiretap Act demand letters
   target sites whose ad/analytics tags (Meta Pixel, Google Ads, GA4,
   Bing UET) fire before the visitor consents. The gate has to be
   REAL: no GTM plugin snippet in <head>, no hardcoded tag scripts.

   Consent model (accept-only):
   - Accept  → first-party cookie `sagan_consent=granted` (12 mo) and
               GTM loads immediately (GA4, Google Ads, Microsoft Ads UET).
   - X close → sessionStorage flag only; banner hides for this
               session, returns next visit. Nothing loads.
   - Global Privacy Control (navigator.globalPrivacyControl) →
               treated as an opt-out: no banner, no GTM.

   CallRail: NOT consent-gated (decision, Igor, 2026-10) because
   dynamic number swapping is how marketing attribution works on
   this site. CallRail acts as our service provider (call
   attribution for us, not its own advertising) and is disclosed in
   the privacy policy. To gate it behind Accept like WebPerfex does,
   flip CALLRAIL_REQUIRES_CONSENT to true — nothing else changes.

   Google Consent Mode v2: gatsby-ssr.js sets every consent type to
   'denied' as the page default; loadGTM() flips them to 'granted'
   right before injecting the container. */

const CONSENT_COOKIE = 'sagan_consent'
const DISMISS_KEY = 'sagan_consent_dismissed'
const CONSENT_MAX_AGE = 60 * 60 * 24 * 365 // 12 months
const GTM_ID = 'GTM-TQ7PHSK'
const CALLRAIL_SRC =
  '//cdn.callrail.com/companies/478918269/6e27eed91953fea9b84a/12/swap.js'
const CALLRAIL_REQUIRES_CONSENT = false

// Matches the removed gatsby-plugin-google-tagmanager option
// includeInDevelopment: false.
const TRACKING_ENABLED = process.env.NODE_ENV === 'production'

const isBrowser = () =>
  typeof window !== 'undefined' && typeof document !== 'undefined'

export const hasGPC = () =>
  isBrowser() && navigator.globalPrivacyControl === true

export const hasConsent = () => {
  if (!isBrowser()) return false
  return document.cookie
    .split(';')
    .some((c) => c.trim() === `${CONSENT_COOKIE}=granted`)
}

export const isDismissedThisSession = () => {
  if (!isBrowser()) return false
  try {
    return window.sessionStorage.getItem(DISMISS_KEY) === '1'
  } catch (e) {
    return false
  }
}

export const dismissForSession = () => {
  if (!isBrowser()) return
  try {
    window.sessionStorage.setItem(DISMISS_KEY, '1')
  } catch (e) {
    // Safari private mode can throw — banner will just re-show.
  }
}

/* ── Loaders ──────────────────────────────────────────────────── */

let gtmLoaded = false
let callRailLoaded = false

const loadGTM = () => {
  if (gtmLoaded || !TRACKING_ENABLED) return
  if (hasGPC() || !hasConsent()) return
  gtmLoaded = true

  window.dataLayer = window.dataLayer || []
  function gtag() {
    window.dataLayer.push(arguments)
  }
  gtag('consent', 'update', {
    ad_storage: 'granted',
    analytics_storage: 'granted',
    ad_user_data: 'granted',
    ad_personalization: 'granted',
  })
  window.dataLayer.push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' })
  const s = document.createElement('script')
  s.async = true
  s.src = `https://www.googletagmanager.com/gtm.js?id=${GTM_ID}`
  document.head.appendChild(s)
}

const loadCallRail = () => {
  if (callRailLoaded || !CALLRAIL_SRC) return
  if (CALLRAIL_REQUIRES_CONSENT && (hasGPC() || !hasConsent())) return
  callRailLoaded = true

  // Loaded only after React has hydrated: the swap rewrites
  // phone-number text, and doing that before hydration makes React
  // throw away the server HTML and re-render the page, or put the
  // original number back.
  const s = document.createElement('script')
  s.src = CALLRAIL_SRC
  s.async = true
  document.body.appendChild(s)
}

/* Called once after hydration (gatsby-browser onInitialClientRender)
   and again on Accept. Each loader checks its own conditions and is
   idempotent. */
export const loadTrackers = () => {
  if (!isBrowser()) return
  loadCallRail()
  loadGTM()
}

/* Accept: persist consent, then load. */
export const grantConsent = () => {
  if (!isBrowser()) return
  document.cookie = `${CONSENT_COOKIE}=granted; max-age=${CONSENT_MAX_AGE}; path=/; SameSite=Lax; Secure`
  loadTrackers()
}
